import { get, post } from '../apiClient';

export interface OrganizationValidationResult {
  isValid: boolean;
  organizationName?: string | undefined;
  ownerEmail?: string | undefined;
  ownerName?: string | undefined;
  existingModules: string[];
  errorMessage?: string | undefined;
}

export interface ValidateModulesResponse {
  allValid: boolean;
  results: ModuleValidationResult[];
}

export interface ModuleValidationResult {
  moduleKey: string;
  sourceId: string;
  isValid: boolean;
  organizationName?: string | undefined;
  blockers: string[];
}

export interface ValidateOrganizationRequest {
  organizationName?: string | undefined;
  amplifyOrganizationUuid?: string | undefined;
}

export interface ValidateModulesRequest {
  modules: Array<{
    moduleKey: string;
    sourceId: string;
  }>;
}

// Bridge validation types
export interface ValidateBridgeRequest {
  bridgeEntityUuid: string;
}

export interface BridgeValidationResponse {
  valid: boolean;
  entity?: BridgeEntityInfo | undefined;
  matchedModules: MatchedModule[];
  unmatchedProducts: UnmatchedProduct[];
  duplicates?: BridgeDuplicateInfo[] | undefined;
  specialModules: MatchedSpecialModule[];
  error?: 'BRIDGE_ENTITY_NOT_FOUND' | 'BRIDGE_NO_MATCHING_MODULES' | 'BRIDGE_MINISTRY_ONE_WITHOUT_GIVING' | string | undefined;
}

export interface BridgeEntityInfo {
  uuid: string;
  name: string;
  addressLine1?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  zip?: string | undefined;
  contactEmail?: string | undefined;
  contactFirstName?: string | undefined;
  contactLastName?: string | undefined;
}

export interface MatchedModule {
  moduleKey: string;
  displayName: string;
  bridgeProductId: string;
  labelId: string;
  productLabel: string;
  bridgeProductTypeId: number;
  duplicateCheck: 'clean' | 'duplicate';
  userMigrationAvailable: boolean;
  amplifyModuleId: number | null;
}

export interface MatchedSpecialModule {
  moduleKey: string;
  displayName: string;
  labelId: string;
  productLabel: string;
  bridgeProductTypeId: number;
  duplicateCheck: 'clean' | 'duplicate';
}

export interface UnmatchedProduct {
  productLabel: string;
  productType: number;
  labelId: string;
  reason: string;
}

export interface BridgeDuplicateInfo {
  moduleKey: string;
  displayName: string;
  labelId: string;
  productType: number;
  conflictingEntities: DuplicateEntityInfo[];
}

export interface DuplicateEntityInfo {
  entityId: string;
  entityName: string;
}

export const validationsApi = {
  validateOrganization: (data: ValidateOrganizationRequest) =>
    get<OrganizationValidationResult>('/validations/organization', data),

  validateModules: (data: ValidateModulesRequest) =>
    // The API validates each module sequentially against its source system, so total time
    // scales with module count and can far exceed the client's default 30s timeout — a
    // multi-module validation was getting canceled by axios mid-flight. 3 min covers the
    // worst case (each module's own outbound timeout still bounds the server side).
    post<ValidateModulesResponse>('/validations/modules', data, { timeout: 180_000 }),

  validateBridge: (data: ValidateBridgeRequest) =>
    post<BridgeValidationResponse>('/validations/bridge', data),
};

// Query key factory
export const validationKeys = {
  all: ['validations'] as const,
  organization: (data: ValidateOrganizationRequest) =>
    [...validationKeys.all, 'organization', data] as const,
  modules: (data: ValidateModulesRequest) =>
    [...validationKeys.all, 'modules', data] as const,
  bridge: (uuid: string) =>
    [...validationKeys.all, 'bridge', uuid] as const,
};
